import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { alpha } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { type ComponentType, useState } from 'react';

import { Input } from 'payments/components/Input';
import { PaymentTextInputAdornment } from 'payments/components/PaymentTextInputAdornment';
import { FONT_NAMES } from 'payments/constants/fonts';
import { type CardFieldState } from 'payments/types/card';
import { SOY } from 'reservations/brand/constants';
import useContrastTextColor from 'reservations/brand/hooks/useContrastTextColor';

export type Props = {
  name: string;
  label: string;
  InputEl: ComponentType;
  fieldState?: CardFieldState;
  cardDeclinedError?: boolean;
};

export const CardField = ({
  InputEl,
  label,
  name,
  fieldState,
  cardDeclinedError = false
}: Props) => {
  const [_error, setError] = useState<string | null>(null);
  const [_isEmpty, setIsEmpty] = useState(true);
  const [_isFocused, setIsFocused] = useState(false);
  const borderColor = useContrastTextColor();
  const error = _error || (fieldState?.error ?? null) || cardDeclinedError;
  const isEmpty = _isEmpty && (fieldState?.isEmpty ?? true);
  const isFocused = _isFocused || (fieldState?.isFocused ?? false);
  const theme = useTheme();

  return (
    <Box>
      <TextField
        name={name}
        label={label}
        variant="outlined"
        size="small"
        error={!!error}
        helperText={error}
        required
        fullWidth
        InputLabelProps={{
          htmlFor: name,
          shrink: isFocused || !isEmpty,
          sx: { marginTop: isFocused || !isEmpty ? '-2px' : '5px' }
        }}
        InputProps={{
          endAdornment: (
            <PaymentTextInputAdornment
              name={name}
              hasError={!!error && !cardDeclinedError}
            />
          ),
          inputComponent: Input,
          inputProps: {
            component: InputEl,
            label: '',
            options: {
              placeholder: '',
              style: {
                base: {
                  fontSize: '16px',
                  fontFamily: FONT_NAMES.reg,
                  color: SOY,
                  padding: '14px 12px'
                },
                invalid: {
                  color: 'inherit'
                }
              }
            },
            onFocus: /* istanbul ignore next */ () => setIsFocused(true),
            onBlur: /* istanbul ignore next */ () => setIsFocused(false),
            onChange: /* istanbul ignore next */ (e: any) => {
              if (e.empty !== isEmpty) {
                setIsEmpty(e.empty);
              }

              const errorMessage = e.error?.message ?? null;
              if (errorMessage !== error) {
                setError(errorMessage);
              }
            }
          }
        }}
        sx={
          borderColor
            ? {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: error
                    ? theme.palette.error.main
                    : alpha(borderColor, 0.8)
                },
                '&:hover': {
                  borderColor: alpha(borderColor, 0.8)
                }
              }
            : {}
        }
      />
    </Box>
  );
};
